/**
 * Centralized logging utility for the Engage application
 * Provides runtime-configurable log levels to reduce console noise
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

/**
 * Global type declaration for window.engageLogLevel
 */
declare global {
  interface Window {
    engageLogLevel?: 'debug' | 'info' | 'warn' | 'error' | 'none';
  }
}

const STORAGE_KEY = 'engageLogLevel';

class Logger {
  private currentLevel: LogLevel = LogLevel.WARN;

  constructor() {
    // Initialize from localStorage first, then window.engageLogLevel
    this.initializeLogLevel();

    // Watch for runtime changes to window.engageLogLevel
    if (typeof window !== 'undefined') {
      // Check periodically for changes (lightweight approach)
      setInterval(() => this.updateLogLevel(), 1000);
    }
  }

  /**
   * Initialize log level from localStorage or window.engageLogLevel
   */
  private initializeLogLevel(): void {
    if (typeof window === 'undefined') return;

    // First, check localStorage for persisted setting
    try {
      const savedLevel = localStorage.getItem(STORAGE_KEY);
      if (savedLevel && this.isValidLogLevel(savedLevel)) {
        this.setLevelFromString(savedLevel);
        // Also set window.engageLogLevel to match
        window.engageLogLevel = savedLevel as any;
        console.info(`[Logger] Restored log level from localStorage: ${savedLevel.toUpperCase()}`);
        return;
      }
    } catch (error) {
      // localStorage might not be available
    }

    // If no localStorage setting, check window.engageLogLevel
    if (window.engageLogLevel && this.isValidLogLevel(window.engageLogLevel)) {
      this.setLevelFromString(window.engageLogLevel);
      this.saveToLocalStorage(window.engageLogLevel);
    }
  }

  /**
   * Update log level from window.engageLogLevel
   */
  private updateLogLevel(): void {
    if (typeof window !== 'undefined' && window.engageLogLevel) {
      const levelString = window.engageLogLevel.toLowerCase();

      if (!this.isValidLogLevel(levelString)) return;

      const levelMap: Record<string, LogLevel> = {
        debug: LogLevel.DEBUG,
        info: LogLevel.INFO,
        warn: LogLevel.WARN,
        error: LogLevel.ERROR,
        none: LogLevel.NONE,
      };

      const newLevel = levelMap[levelString];
      if (newLevel !== undefined && newLevel !== this.currentLevel) {
        this.currentLevel = newLevel;
        this.saveToLocalStorage(levelString);
        console.info(`[Logger] Log level changed to: ${levelString.toUpperCase()}`);
      }
    }
  }

  /**
   * Check if a string is a valid log level
   */
  private isValidLogLevel(level: string): boolean {
    const validLevels = ['debug', 'info', 'warn', 'error', 'none'];
    return validLevels.includes(level.toLowerCase());
  }

  /**
   * Set log level from string
   */
  private setLevelFromString(level: string): void {
    const levelMap: Record<string, LogLevel> = {
      debug: LogLevel.DEBUG,
      info: LogLevel.INFO,
      warn: LogLevel.WARN,
      error: LogLevel.ERROR,
      none: LogLevel.NONE,
    };

    const newLevel = levelMap[level.toLowerCase()];
    if (newLevel !== undefined) {
      this.currentLevel = newLevel;
    }
  }

  /**
   * Save log level to localStorage
   */
  private saveToLocalStorage(level: string): void {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(STORAGE_KEY, level);
    } catch (error) {
      // localStorage might not be available
    }
  }

  /**
   * Get current log level
   */
  getLevel(): LogLevel {
    return this.currentLevel;
  }

  /**
   * Set log level programmatically
   */
  setLevel(level: LogLevel): void {
    this.currentLevel = level;

    // Update window.engageLogLevel and localStorage to match
    const levelStrings: Record<LogLevel, string> = {
      [LogLevel.DEBUG]: 'debug',
      [LogLevel.INFO]: 'info',
      [LogLevel.WARN]: 'warn',
      [LogLevel.ERROR]: 'error',
      [LogLevel.NONE]: 'none',
    };

    const levelString = levelStrings[level];
    if (levelString && typeof window !== 'undefined') {
      window.engageLogLevel = levelString as any;
      this.saveToLocalStorage(levelString);
    }
  }

  /**
   * Log debug message (most verbose)
   * Use for detailed debugging information
   */
  debug(...args: any[]): void {
    if (this.currentLevel <= LogLevel.DEBUG) {
      console.log(...args);
    }
  }

  /**
   * Log informational message
   * Use for general informational messages
   */
  info(...args: any[]): void {
    if (this.currentLevel <= LogLevel.INFO) {
      console.log(...args);
    }
  }

  /**
   * Log warning message
   * Use for warnings that don't prevent operation
   */
  warn(...args: any[]): void {
    if (this.currentLevel <= LogLevel.WARN) {
      console.warn(...args);
    }
  }

  /**
   * Log error message
   * Use for errors and exceptions
   */
  error(...args: any[]): void {
    if (this.currentLevel <= LogLevel.ERROR) {
      console.error(...args);
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Export convenience function for setting log level at runtime
export function setLogLevel(level: 'debug' | 'info' | 'warn' | 'error' | 'none'): void {
  if (typeof window !== 'undefined') {
    window.engageLogLevel = level;
    // The logger will pick this up on its next update cycle and save to localStorage
  }
}

// Export convenience function for clearing the persisted log level
export function clearLogLevel(): void {
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem(STORAGE_KEY);
      delete window.engageLogLevel;
      console.info('[Logger] Cleared persisted log level - will use default (WARN) on next page load');
    } catch (error) {
      // localStorage might not be available
    }
  }
}
